<template lang="pug">
  div(class="dynamic-select-wrapper")
    button(
      ref="select"
      class="dynamic-select__activator"
      :disabled="disabled"
      :class="selectButtonClasses"
      @click.stop="onMenuOpen"
    )
      template(v-if="!toolbarsMap[field.id]")
        span(
          class="text-12px dark dynamic-select__activator-text"
          :class="{ 'color-primary': !canEdit && selectedOption, errored: showErrors }"
          :style="selectTextStyles"
        ) {{ selectedText }}
        e-svg-icon(
          size="sm"
          :name="showErrors ? 'arrow-down-error' : 'arrow-down-dark-gray'"
          v-if="!xsDevice"
        )
      div(
        v-else
        data-dynamic-select="body"
        class="dynamic-select__body"
      )
        div(class="dynamic-select__input-wrapper")
          input(
            class="dynamic-select__input text-12px"
            :readonly="!canEdit"
            v-model="inputValue"
            @blur="onBlur"
          )
          v-btn(
            v-if="selectedOption"
            small
            icon
            class="dynamic-select__input-icon"
            @click.stop="removeSelectedOption"
          )
            e-svg-icon(size="sm") close
        div(class="mt-2")
          div(
            v-for="option in options"
            :key="option.id"
            class="dynamic-select__options-item"
          )
            button(
              @click.stop="handleSelect(option)"
              class="d-flex align-center w-100"
            )
              span(class="d-flex align-center")
                e-svg-icon(
                  :name="isSelectedOption(option.id) && !canEdit ? 'selected-radio-button' : 'circle-empty'"
                  class="dynamic-select__options-item-icon"
                )
              input(
                ref="optionInput"
                class="dynamic-select__options-input text-12px"
                :readonly="!canEdit"
                v-model="option.value"
                :placeholder="$t('Add variant')"
                @blur="saveOption"
              )
            button(
              v-if="canEdit"
              class="dynamic-select__options-btn"
              @click="deleteOption(option.id)"
            )
              e-svg-icon circle-close
        div(
          v-if="canEdit"
          class="dynamic-select__options-item justify-start"
        )
          e-svg-icon(class="mr-2") circle-empty
          button(
            class="text-12px"
            @click="addOption"
          ) {{ $t('Add variant') }}
    div(
      v-if="showErrors"
      class="dynamic-select__error"
    ) {{ errorText }}
</template>

<script>
import { mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import responsive from '~/mixins/pages/responsive'

export default {
  name: 'TemplatesDynamicSelect',
  mixins: [responsive],
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    isResizing: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      inputValue: null,
      options: [],
      selectedOption: null,
      showErrors: false
    }
  },
  computed: {
    ...mapGetters('templatefields', {
      toolbarsMap: 'toolbarsMap'
    }),
    selectedText() {
      return this.selectedOption?.value || this.inputValue
    },
    selectButtonClasses() {
      return {
        filled: this.selectedOption,
        disabled: this.field.documentFieldPreValue,
        opened: this.toolbarsMap[this.field.id],
        'error-state': this.showErrors || this.hasError
      }
    },
    selectTextStyles() {
      return {
        fontSize: this.xsDevice ? '7px' : this.fontSize + 'px'
      }
    },
    hasEmptyOptions() {
      return this._.some(this.options, option => !option.value)
    },
    hasLessMinimumRequiredOptions() {
      return this._.get(this.options, 'length') < 2
    },
    hasErrors() {
      return this.hasEmptyOptions || this.hasLessMinimumRequiredOptions || this.hasError
    },
    errorText() {
      if (this.hasLessMinimumRequiredOptions) {
        return this.$t('Add minimum 2 variants')
      }
      if (this.hasEmptyOptions) {
        return this.$t('Need to fill all fields')
      }
      return null
    },
    disabled() {
      return this.field.documentFieldPreValue || this.isDisabled
    }
  },
  watch: {
    selectedOption(val) {
      if (val) {
        this.inputValue = val.value
      } else {
        this.inputValue = this.placeholder
      }
      if (val?.value !== this.value) {
        this.$emit('update:value', this._.isObject(val) ? val.value : val)
      }
    },
    value(value) {
      if (value) {
        this.selectedOption = this._.find(this.options, option => option.value === value)
      }
    },
    hasErrors(val) {
      if (!val) {
        this.$store.commit('templatefields/REMOVE_ERROR_FIELD_IDS', this.field.key)
        this.showErrors = false
      }
    }
  },
  mounted() {
    this.setSelect()
  },
  methods: {
    validate() {
      return new Promise(resolve => {
        this.showErrors =
          this.hasEmptyOptions || this.hasLessMinimumRequiredOptions || this.hasError
        if (this.showErrors) {
          this.$store.commit('templatefields/SET_ERROR_FIELD_IDS', this.field.key)
          resolve(false)
        } else {
          resolve(true)
        }
      })
    },
    closeBody() {
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: this.field.id,
        value: false
      })
    },
    handleSelect(option) {
      if (!this.canEdit) {
        this.selectedOption = option
        this.closeBody()
      }
    },
    isSelectedOption(optionId) {
      return this.selectedOption && this.selectedOption.id === optionId
    },
    addOption() {
      this.options.push({ id: uuidv4(), value: null })
      this.$nextTick(() => {
        const lastOptionInput = this.$refs.optionInput[this.options.length - 1]
        lastOptionInput.focus()
      })
    },
    saveOption() {
      this.$store.commit('templatefields/UPDATE_FIELD', {
        id: this.field.id,
        updatedData: {
          options: this._.map(this.options, option => option.value),
          changed: true
        }
      })
    },
    deleteOption(id) {
      this.options = this._.filter(this.options, option => option.id !== id)
      const optionsValues = this._.map(this.options, option => option.value)
      const isChanged = !!this._.get(this._.difference(this.field.options, optionsValues), 'length')
      this.$store.commit('templatefields/UPDATE_FIELD', {
        id: this.field.id,
        updatedData: {
          options: optionsValues,
          changed: isChanged
        }
      })
    },
    removeSelectedOption() {
      this.selectedOption = null
    },
    onMenuOpen() {
      this.$emit('wasOpened', this.field.id)
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: this.field.id,
        value: true
      })
    },
    onBlur() {
      this.$store.commit('templatefields/SET_LAST_ACTIVE_FIELD_ID', this.field.id)
      if (this.inputValue !== this.field.hint && this.canEdit) {
        this.$store.commit('templatefields/UPDATE_FIELD', {
          id: this.field.id,
          updatedData: {
            hint: this.inputValue,
            changed: true
          }
        })
      }
    },
    onKeyDown(e) {
      if (e.ctrlKey && e.key === 'c') {
        this.$emit('copy')
      }
      if (e.ctrlKey && e.key === 'x') {
        this.$emit('cut')
      }
      if (e.key === 'Delete') {
        this.$emit('delete')
      }
    },
    setSelect() {
      this.$nextTick(() => {
        this.options = this._.map(this.field.options, option => ({ id: uuidv4(), value: option }))

        if (this.canEdit) {
          this.inputValue = this.field.hint
        } else if (this.field.documentFieldPreValue) {
          this.selectedOption = { id: uuidv4(), value: this.field.documentFieldPreValue }
        } else if (this.field.documentFieldValue) {
          this.selectedOption = { id: uuidv4(), value: this.field.documentFieldValue }
        } else {
          this.inputValue = this.placeholder
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-select {
  &-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }
  &__body {
    width: 100%;
  }

  &__activator {
    display: flex;
    align-items: center;
    justify-content: space-between;

    overflow: auto;

    width: 100%;
    height: 100%;

    padding: 5px 8px;

    border: 1px solid $primary;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.7);

    @media (max-width: map-get($breakpoints, 'sm')) {
      padding: 0;
      border-radius: 4px;
      font-size: 7px;
      display: flex;
      justify-content: center;
    }

    &.filled {
      background:
        linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
        rgba(255, 255, 255, 0.4);
    }
    &.opened {
      height: auto;
      background: $white;
    }
    &.error-state {
      border: 1px solid $danger;
      background: rgba(255, 23, 68, 0.12);
    }
  }

  &__activator-text {
    word-break: break-word;
    text-align: left;
    font-weight: 300;

    @media (max-width: map-get($breakpoints, 'sm')) {
      line-height: 1.5;
    }
  }

  &__input-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
  }

  &__input {
    width: 100%;
    height: 32px;

    padding: 8px;
    border-radius: 8px;
    font-weight: 300;

    outline: none;
    background: $background-secondary-alternative;

    @media (max-width: map-get($breakpoints, 'sm')) {
      font-size: 7px;
      padding: 4px;
      height: 16px;
    }
  }

  &__input-icon {
    width: 16px;
    height: 16px;

    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    z-index: map-get($zIndexes, 'base');
  }

  &__options-item {
    width: 100%;
    height: 32px;

    padding: 6px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    &-icon {
      @media (max-width: map-get($breakpoints, 'sm')) {
        max-width: 12px;
        max-height: 12px;
      }
    }
  }

  &__options-input {
    width: 100%;
    height: 100%;

    margin-left: 8px;

    outline: none;
    font-weight: 300;

    @media (max-width: map-get($breakpoints, 'sm')) {
      font-size: 7px;
      margin-left: 4px;
    }
  }

  &__options-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__error {
    font-size: 12px;
    color: $danger;

    @media (max-width: map-get($breakpoints, 'xs')) {
      font-size: 7px;
    }
  }
}
</style>
